/**
 * Tenants — EVT platform administrators: every company on the portal, and the
 * form that creates one with its first administrator. The id is the contractor
 * id fr-docs and mail-reader already use; the page says so, because a typo here
 * produces a company whose people see nothing.
 *
 * A company created here has no flow until one is chosen for it, so creating it
 * opens its page, where the Flows card is.
 */
import { Badge, Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { createTenant, listTenants } from '../../api/access'
import { useSession } from '../../auth/useSession'
import { parseEmailList } from '../../components/access/AccessPanel'
import { describeApiError, useI18n } from '../../i18n'

export const platformTenantsKey = ['portal', 'platform', 'tenants'] as const

export function TenantsPage() {
  const { t, locale } = useI18n()
  const { platformAdmin, strongAuth } = useSession()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const tenants = useQuery({ queryKey: platformTenantsKey, queryFn: listTenants, enabled: platformAdmin && strongAuth })

  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [firstAdmin, setFirstAdmin] = useState('')
  const [emailCode, setEmailCode] = useState(false)
  const [domains, setDomains] = useState('')
  // The first administrator's language, not the operator's: EVT writes in English to a Brazilian company's admin.
  const [mailLocale, setMailLocale] = useState<'en' | 'pt-BR'>(locale === 'en' ? 'en' : 'pt-BR')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const onCreate = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setDone(null)
    try {
      const r = await createTenant({
        id: id.trim(),
        name: name.trim(),
        settings: { emailCodeLogin: emailCode, allowedEmailDomains: parseEmailList(domains) },
        ...(firstAdmin.trim() ? { firstAdminEmail: firstAdmin.trim() } : {}),
        locale: mailLocale
      })
      setDone(t.access.created(r.tenant.name))
      await qc.invalidateQueries({ queryKey: platformTenantsKey })
      // Next step: its flows. The company's page says so (readiness) and holds the Flows card.
      navigate(`/platform/tenants/${encodeURIComponent(r.tenant.id)}`)
    } catch (err) {
      setError(describeApiError(t, err))
    } finally {
      setBusy(false)
    }
  }

  if (!platformAdmin) {
    return (
      <div className="page portal-page">
        <Notice kind="warning">{t.errors.accessForbidden}</Notice>
      </div>
    )
  }

  return (
    <div className="page portal-page access-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t.access.tenantsTitle}</h1>
          <div className="page-sub">{t.access.tenantsSubtitle}</div>
        </div>
      </div>
      {!strongAuth && <Notice kind="info">{t.access.stepUp}</Notice>}

      <section className="card">
        <div className="card-head">
          <div className="card-title">{t.access.createTitle}</div>
        </div>
        <form className="card-pad access-invite" onSubmit={(e) => void onCreate(e)}>
          <p className="muted access-help">{t.access.createHelp}</p>
          <div className="access-invite-row">
            <label className="field">
              <span className="field-label">{t.access.fieldId}</span>
              <input
                className="input mono"
                value={id}
                placeholder={t.access.fieldIdPlaceholder}
                onChange={(e) => setId(e.target.value)}
                required
              />
            </label>
            <label className="field">
              <span className="field-label">{t.access.fieldName}</span>
              <input
                className="input"
                value={name}
                placeholder={t.access.fieldNamePlaceholder}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </label>
            <label className="field">
              <span className="field-label">{t.access.fieldFirstAdmin}</span>
              <input
                className="input"
                type="email"
                value={firstAdmin}
                placeholder="ana@company.com"
                onChange={(e) => setFirstAdmin(e.target.value)}
              />
            </label>
          </div>
          <label className="field">
            <span className="field-label">{t.access.fieldDomains}</span>
            <input
              className="input"
              value={domains}
              placeholder="heineken.com.br"
              onChange={(e) => setDomains(e.target.value)}
            />
            <span className="field-help">{t.access.fieldDomainsHelp}</span>
          </label>
          <label className="field access-narrow">
            <span className="field-label">{t.access.inviteLanguage}</span>
            <select
              className="select"
              value={mailLocale}
              onChange={(e) => setMailLocale(e.target.value as 'en' | 'pt-BR')}
            >
              <option value="pt-BR">{t.common.languageNamePt}</option>
              <option value="en">{t.common.languageNameEn}</option>
            </select>
          </label>
          <label className="access-check">
            <input type="checkbox" checked={emailCode} onChange={(e) => setEmailCode(e.target.checked)} />
            <span>{t.access.fieldEmailCode}</span>
          </label>
          <p className="muted access-help">{t.access.fieldEmailCodeHelp}</p>
          <p className="muted access-help">{t.access.createFlowsHelp}</p>
          <div>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !strongAuth || !id || !name}>
              {busy ? <Spinner size={13} /> : <Icon name="plus" size={13} />} {t.access.createSubmit}
            </button>
          </div>
          {error && <Notice kind="danger">{error}</Notice>}
          {done && <Notice kind="success">{done}</Notice>}
        </form>
      </section>

      <section className="card">
        {tenants.isLoading ? (
          <div className="card-pad">
            <Spinner size={16} />
          </div>
        ) : tenants.error ? (
          <div className="card-pad">
            <Notice kind="danger">{describeApiError(t, tenants.error)}</Notice>
          </div>
        ) : (tenants.data ?? []).length === 0 ? (
          <p className="card-pad muted">{t.access.tenantsEmpty}</p>
        ) : (
          <div className="access-table-wrap">
            <table className="tbl access-table">
              <thead>
                <tr>
                  <th>{t.access.colTenant}</th>
                  <th>{t.access.colMembers}</th>
                  <th>{t.access.colPolicy}</th>
                  <th>{t.access.colFlows}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(tenants.data ?? []).map((tn) => (
                  <tr key={tn.id} className={tn.status === 'suspended' ? 'is-revoked' : undefined}>
                    <td>
                      <div className="access-person">
                        <span>{tn.name}</span>
                        {tn.status === 'suspended' && <Badge kind="disabled">{t.access.statusSuspended}</Badge>}
                      </div>
                      <div className="muted access-sub mono">
                        {tn.id}
                        {!tn.registered && ` · ${t.access.notRegistered}`}
                      </div>
                    </td>
                    <td className="access-sub">
                      {t.access.countsLine(tn.counts.active, tn.counts.admins, tn.counts.invited)}
                    </td>
                    <td className="access-sub">
                      {tn.settings.emailCodeLogin ? t.access.policyEmailCode : t.access.policyPasskey}
                    </td>
                    <td className="access-sub">
                      {tn.flowAccess.version === 0
                        ? t.access.flowsAllShortUnchosen
                        : tn.flowAccess.mode === 'all'
                          ? t.access.flowsAllShort
                          : t.access.flowsChosenShort(tn.flowAccess.flowIds.length)}
                    </td>
                    <td>
                      <Link className="btn btn-ghost btn-sm" to={`/platform/tenants/${encodeURIComponent(tn.id)}`}>
                        {t.access.manage}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
